import React, { useState, useEffect } from 'react';

import { StyledWrapper } from './styles';
import { Status, ImportStatus } from './types';
import AudienceImportStatusContainer from './import_status_container';
import ExportStatusContainer from './export_status_container';
import SectionMessageContainer from './section_message_container';
import { ImportUsersForm } from '../../components/manage/audience/import_users_form';

interface AudienceCsvStatusContainerProps {
  initialStatus: Status;
  processImportUrl: string;
  downloadTemplateUrl: string;
  filestackKey: string;
  trialPage: boolean;
  mode: string;
}

// The current id we're using for polling. If it's undefined, that should mean
// we aren't currently polling
let pollingIntervalId: number | undefined;
const pollingIntervalLengthMs = 3000;

export default function AudienceCsvStatusContainer({
  initialStatus,
  processImportUrl,
  downloadTemplateUrl,
  filestackKey,
  trialPage,
  mode,
}: AudienceCsvStatusContainerProps) {
  const [status, setStatus] = useState(initialStatus);
  const [pollingError, setPollingError] = useState(false);
  const [genericError, setGenericError] = useState(false);

  // Anything that needs to happen when `status` changes
  useEffect(() => {
    if (
      status == null ||
      pollingError ||
      (mode === 'import' && !status.show_progress)
    ) {
      // Stop polling
      clearInterval(pollingIntervalId);
      pollingIntervalId = undefined;
      return;
    }

    pollingIntervalId = window.setInterval(() => {
      // Make the request to the backend to fetch status
      $.ajax({
        method: 'POST',
        url: status.urls.status,
        success: function(data) {
          if (mode === 'import' && data.import_status === 'complete') {
            // refresh the static lists below
            window.location.reload();
          } else {
            setStatus(data);
          }
        },
        error: function() {
          setPollingError(true);
        },
      });
    }, pollingIntervalLengthMs);

    return function() {
      clearInterval(pollingIntervalId);
    };
  }, [status]);

  function PollingErrorMesage() {
    let pollingErrorMessage = `Your ${mode} is taking longer than expected, but it’s still in progress. Return to this page later to see when your ${mode} is complete.`;
    return (
      <SectionMessageContainer
        appearance="warning"
        message={pollingErrorMessage}
      />
    );
  }

  function renderStatusContent() {
    if (pollingError) {
      return <PollingErrorMesage />;
    } else if (genericError) {
      return (
        <SectionMessageContainer
          appearance="error"
          message="There was an error processing your import. Re-upload your file and try to import again."
        />
      );
    }
    if (status == null) return;

    if (mode == 'import') {
      return (
        <StyledWrapper id="audience-import-status">
          <AudienceImportStatusContainer
            status={status as ImportStatus}
            downloadTemplateUrl={downloadTemplateUrl}
          />
        </StyledWrapper>
      );
    } else {
      return (
        <StyledWrapper id="audience-export-status">
          <ExportStatusContainer status={status} />
        </StyledWrapper>
      );
    }
  }

  return (
    <>
      {renderStatusContent()}
      <ImportUsersForm
        processImportUrl={processImportUrl}
        downloadTemplateUrl={downloadTemplateUrl}
        filestackKey={filestackKey}
        setImportStatus={setStatus}
        setErrorStatus={setGenericError}
        trialPage={trialPage}
      />
    </>
  );
}
